import { useState, useEffect, useRef, useMemo } from 'react';
import { safeJsonParse, sortPendingCartItemsByHoldStatus } from '@utility/helperFunction';

const EMPTY = [];

const combinePackItems = items => {
  if (!items || items.length === 0) return items;
  const qtySums = new Map();
  items.forEach(item => {
    const id = safeJsonParse(item.modifierJson, {}).internalId || null;
    if (id) qtySums.set(id, (qtySums.get(id) || 0) + (item.quantity || 0));
  });
  const seen = new Set();
  return items.reduce((result, item) => {
    const id = safeJsonParse(item.modifierJson, {}).internalId || null;
    if (id) {
      if (!seen.has(id)) {
        seen.add(id);
        result.push({ ...item, quantity: qtySums.get(id) });
      }
    } else {
      result.push(item);
    }
    return result;
  }, []);
};

const filterPendingBySeat = (items, position, menuItemsHashTable) => {
  if (!position || position.number === -1) return items;
  return items.filter(
    v =>
      (!v.isLinked || menuItemsHashTable?.[v.menuItemId]?.cardType === 1) &&
      v.seatIds?.some(sId => sId === position.id)
  );
};

const filterSentBySeat = (items, position) => {
  if (!position || position.number === -1) return items;
  return items.filter(v => v.seats?.some(s => s.seatId === position.id));
};

const getDiscountItems = (discounts, position, isOnlyOrderMode) => {
  if (!Array.isArray(discounts) || discounts.length === 0) return EMPTY;
  const discountItems = [];
  if (isOnlyOrderMode && position && position.number !== -1) {
    discounts.forEach(itm => {
      const singleSeat = itm.seats?.find(s => s?.seatId === position?.id);
      if (singleSeat) {
        discountItems.push({
          ...itm,
          id: itm.id ?? `discount-${itm.discountId ?? Math.random()}`,
          price: singleSeat?.splitAmount ?? itm.value,
          menuItemName: itm.discountName,
          isDiscount: true,
          seatIds: [position.id],
        });
      }
    });
  } else {
    discounts.forEach((itm, idx) => {
      const seatIds = Array.isArray(itm.seats)
        ? itm.seats.map(s => s?.seatId).filter(Boolean)
        : [];
      discountItems.push({
        ...itm,
        id: itm.id ?? `discount-${itm.discountId ?? idx}`,
        price: itm.value ?? itm.price,
        menuItemName: itm.discountName,
        isDiscount: true,
        seatIds,
      });
    });
  }
  return discountItems;
};

const getServiceChargeItems = (serviceCharges, position, isOnlyOrderMode) => {
  if (!Array.isArray(serviceCharges) || serviceCharges.length === 0) return EMPTY;
  const serviceChargeItems = [];

  serviceCharges.forEach((itm, idx) => {
    const isSpecificSeat = position && position.number !== -1;
    const hasSeatsArray = Array.isArray(itm.seats) && itm.seats.length > 0;

    let singleSeat = null;
    if (isSpecificSeat && hasSeatsArray) {
      singleSeat = itm.seats.find(s => s?.seatId === position?.id);
      if (!singleSeat) return; // Assigned to another seat
    }

    const price = singleSeat?.splitAmount ?? itm.price ?? itm.amount ?? itm.serviceChargeAmount ?? itm.value ?? 0;
    const menuItemName = itm.serviceChargeName || itm.name || 'Service Charge';
    const seatIds = hasSeatsArray ? itm.seats.map(s => s?.seatId).filter(Boolean) : (isSpecificSeat ? [position.id] : []);

    serviceChargeItems.push({
      ...itm,
      id: itm.id ?? `serviceCharge-${itm.serviceChargeId ?? idx}`,
      price,
      menuItemName,
      isServiceCharge: true,
      seatIds,
    });
  });

  return serviceChargeItems;
};

export const useCartTab = (seat, options = {}) => {
  const { position, isOnlyOrderMode, menuItemsHashTable } = options;

  const rawPendingItems = seat?.PendingItems ?? EMPTY;
  const rawSentItems = seat?.Items ?? EMPTY;

  const pendingItems = useMemo(() => {
    const filtered = !isOnlyOrderMode
      ? rawPendingItems
      : filterPendingBySeat(rawPendingItems, position, menuItemsHashTable);
    return filtered?.length > 0 ? [...filtered].reverse() : EMPTY;
  }, [isOnlyOrderMode, rawPendingItems, position, menuItemsHashTable]);

  const sentItems = useMemo(() => {
    const itemsList = !isOnlyOrderMode
      ? rawSentItems
      : filterSentBySeat(rawSentItems, position);
    const discountItems = getDiscountItems(seat?.discounts, position, isOnlyOrderMode);
    const serviceChargeItems = getServiceChargeItems(seat?.serviceCharges, position, isOnlyOrderMode);
    if (discountItems.length === 0 && serviceChargeItems.length === 0) return itemsList;
    return [...itemsList, ...serviceChargeItems, ...discountItems];
  }, [isOnlyOrderMode, rawSentItems, position, seat?.discounts, seat?.serviceCharges]);

  const [activeTab, setActiveTab] = useState(
    pendingItems.length > 0 ? 'pending' : 'sent'
  );

  useEffect(() => {
    if (pendingItems.length > 0) setActiveTab('pending');
    else if (sentItems.length > 0) setActiveTab('sent');
  }, [seat?.id, pendingItems?.length]);

  // Auto-switch to pending tab when new pending items appear while on sent tab
  const prevPendingLen = useRef(pendingItems.length);
  useEffect(() => {
    if (activeTab === 'sent' && pendingItems.length > prevPendingLen.current) {
      setActiveTab('pending');
    }
    prevPendingLen.current = pendingItems.length;
  }, [pendingItems.length, activeTab]);

  const items = useMemo(
    () => {
      if (activeTab !== 'pending') return sentItems;
      return combinePackItems(sortPendingCartItemsByHoldStatus(pendingItems));
    },
    [activeTab, pendingItems, sentItems]
  );

  return { activeTab, setActiveTab, items, pendingItems, sentItems };
};
